import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Check, RotateCcw, Trash2, CalendarDays, UserRound, ChevronDown} from "lucide-react";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { useToast } from "../../components/Toast";
import { api } from "../../api/client";
import { useAuth } from "../auth/useAuth";
import type { Todo, TodoList, UserPick } from "../../api/types";

// 首頁的代辦事項(業主 2026-09-17:「代辦事項(老闆可交辦事項/同事可互相TAG/
// 自己能當備忘錄)」)。
//
// ==========================================================================
// 兩種事在**同一份清單**上(業主第二輪:「區塊太複雜了」)
// ==========================================================================
// > 代辦事項 區塊太複雜了 我要做的/交辦出去的 都在一起 用顏色區分
//
// 原本是兩個分頁,要按一下才看得到另一半 —— 而「我今天要做什麼」與
// 「我交辦出去的還有誰沒做」本來就要一起看。所以合成一份,用顏色分。
//
// **「用顏色區分」= 顏色加上文字。** 顏色一眼就分得出來,這正是業主要的;
// 但只有顏色的話,色盲、灰階列印、螢幕調過色的人完全分不出來 ——
// 而分不出來的後果是把別人的事當成自己的。所以每一列都帶一枚寫著字的徽章。
//
// ==========================================================================
// 三種用法是同一件事
// ==========================================================================
// 老闆交辦、同事互相交辦、自己的備忘錄 —— 差別只在指派給誰。
// 所以只有一個輸入框,旁邊一個「給誰」的下拉,預設是自己。
//
// **預設自己**是刻意的:逼人每次都選一次「我自己」,備忘錄就不會有人用了。
//
// ==========================================================================
// 完成由被交辦的人自己勾(業主選的)
// ==========================================================================
// 交辦的人勾不動 —— 勾得動的話「做完了」就變成兩方各說各話。
// 後端也擋(403),這裡只是不畫那顆鈕。
//
// 不用 react-query:與 `AttachmentPanel`、`ProjectCost` 同一個慣例。

/** 一列是哪一種。**兩種都寫得出字**,不是只有顏色。 */
type Lane = "mine" | "assigned";

const LANE_LABEL: Record<Lane, string> = {
  mine: "我要做的",
  assigned: "我交辦出去的",
};

/** 左側那一條色帶。文字徽章在同一列上,這裡只是讓它更快被掃到。 */
const LANE_EDGE: Record<Lane, string> = {
  mine: "border-l-[3px] border-l-[var(--accent)]",
  assigned: "border-l-[3px] border-l-[var(--warn)]",
};

interface Row extends Todo {
  lane: Lane;
}

/** 「給誰」下拉裡代表「所有人」的那個值。不是任何人的 id,所以不會撞到。 */
const ALL = "ALL";

export function TodoPanel() {
  const [rows, setRows] = useState<Row[]>([]);
  // 哪幾件廣播被點開了名單。**預設全部收著** —— 那張清單要回答的是
  // 「我交辦出去的事情辦得怎麼樣」,名單是追問的下一層。
  const [openList, setOpenList] = useState<number[]>([]);
  const [people, setPeople] = useState<UserPick[]>([]);
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState<string>("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const { user } = useAuth();

  const refresh = useCallback(() => {
    const load = (scope: Lane) =>
      api
        .get<TodoList>("/todos", { scope })
        // 形狀要擋 —— 拿到不是預期的東西直接 `.items.map()` 會讓整頁白掉。
        .then((got) => (got && Array.isArray(got.items) ? got.items : []))
        .catch(() => [] as Todo[]);

    // 兩支一起發。串著發的話,清單會先跳出一半再補上另一半。
    void Promise.all([load("mine"), load("assigned")]).then(([mine, assigned]) => {
      setRows([
        ...mine.map((one) => ({ ...one, lane: "mine" as Lane })),
        // 交辦出去的排在後面:先回答「我今天要做什麼」,再回答「誰還沒做」。
        ...assigned.map((one) => ({ ...one, lane: "assigned" as Lane })),
      ]);
    });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    api
      .get<UserPick[]>("/users/pickable")
      .then((got) => setPeople(Array.isArray(got) ? got : []))
      .catch(() => setPeople([]));
  }, []);

  const add = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post<Todo>("/todos", {
        title,
        // 廣播不帶 assignee_id —— 帶了的話後端會當成一般交辦,只丟給那一個人。
        ...(assignee === ALL
          ? { to_everyone: true }
          : assignee
            ? { assignee_id: Number(assignee) }
            : {}),
        ...(due ? { due_on: due } : {}),
      });
      setTitle("");
      setDue("");
      refresh();
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "加不進去", "error");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (todo: Todo) => {
    try {
      if (todo.is_done) await api.del(`/todos/${todo.id}/done`);
      else await api.post(`/todos/${todo.id}/done`, {});
      refresh();
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "改不動", "error");
    }
  };

  const remove = async (todo: Todo) => {
    try {
      await api.del(`/todos/${todo.id}`);
      refresh();
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "刪不掉", "error");
    }
  };

  /** 勾得動嗎:**被交辦的人**與老闆 —— 與後端的 `_may_finish()` 同一條規則。 */
  const mayFinish = (todo: Todo): boolean =>
    user !== null && (todo.assignee_id === user.id || user.role === "OWNER");

  const overdue = useMemo(() => rows.filter((one) => one.is_overdue).length, [rows]);

  return (
    <section
      aria-label="代辦事項"
      className="flex min-w-0 flex-col gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 flex items-center gap-2 text-base font-semibold text-[var(--text)]">
          代辦事項
          {overdue > 0 ? <Badge tone="danger">{`${overdue} 件逾期`}</Badge> : null}
        </h2>
        {/* **不另外畫圖例。** 每一列旁邊那一枚徽章本身就寫著是哪一種 ——
            再放一份圖例只是把同樣的字在同一塊裡寫兩遍。 */}
      </div>

      <form
        aria-label="新增代辦事項"
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        {/* **「要做什麼」是唯一真正要打字的那一格,所以它吃掉剩下的寬度。**
            業主 2026-09-17 的截圖裡它反而最窄,整張表單在窄區塊裡折成三行。 */}
        <div data-todo-field="title" className="min-w-[8rem] flex-1">
          <Input
            aria-label="要做什麼"
            placeholder="要做什麼"
            className="w-full"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {/* 兩個標籤換成圖示(業主:「甚麼時候好用一個日曆ICON代替 給誰用一個
            人的ICON代替」)。**圖示不是無障礙名稱** —— 每一格照樣帶 aria-label,
            螢幕閱讀器聽到的字一個都沒有少;圖示一律 aria-hidden。 */}
        <div data-todo-field="assignee" className="flex shrink-0 items-center gap-1">
          <UserRound aria-hidden="true" size={16} className="text-[var(--text-3)]" />
          <Select
            aria-label="給誰"
            title="給誰"
            className="w-28"
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            options={[
              // **預設自己** —— 見檔頭。
              { value: "", label: "我自己" },
              // 業主 2026-09-23:「對象追加 所有人(每個人都可以用這一個)」。
              // 排在「我自己」後面、其他人前面:它是對象的一種,不是另一個功能。
              { value: ALL, label: "所有人" },
              ...people
                .filter((one) => one.id !== user?.id)
                .map((one) => ({ value: String(one.id), label: one.name || one.email })),
            ]}
          />
        </div>

        <div data-todo-field="due" className="flex shrink-0 items-center gap-1">
          <CalendarDays aria-hidden="true" size={16} className="text-[var(--text-3)]" />
          <Input
            aria-label="什麼時候要好"
            title="什麼時候要好(可不填)"
            type="date"
            className="w-36"
            value={due}
            onChange={(e) => setDue(e.target.value)}
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          loading={busy}
          disabled={!title.trim()}
          icon={<Plus aria-hidden="true" size={14} />}
        >
          加進去
        </Button>
      </form>

      {rows.length === 0 ? (
        <p className="m-0 text-sm text-[var(--text-3)]">目前沒有待辦事項。</p>
      ) : (
        <ul aria-label="代辦清單" className="m-0 flex flex-col gap-1 p-0">
          {rows.map((todo) => (
            <li
              key={`${todo.lane}-${todo.id}`}
              className={
                "flex list-none flex-wrap items-center gap-2 rounded-[var(--radius)] border " +
                "border-[var(--border)] bg-[var(--surface)] px-3 py-2 " +
                LANE_EDGE[todo.lane]
              }
            >
              {/* 顏色**加上**文字 —— 見檔頭。 */}
              <Badge tone={todo.lane === "mine" ? "accent" : "warn"}>{LANE_LABEL[todo.lane]}</Badge>

              <span className="min-w-0 flex-1 text-sm text-[var(--text)]">{todo.title}</span>

              {todo.due_on ? (
                // 逾期一律「顏色 + 文字」,不可只靠顏色。
                <Badge tone={todo.is_overdue ? "danger" : "neutral"}>
                  {todo.is_overdue ? `${todo.due_on} 逾期` : todo.due_on}
                </Badge>
              ) : null}

              {/* 廣播在交辦端收合成一行(業主 2026-09-23 選的):清單上只寫
                  「3/5 完成」,點開才是名單。一人一行全部攤開的話,丟一件廣播
                  清單就胖五行 —— 而那張清單要回答的是「辦得怎麼樣」,不是「有誰」。 */}
              {todo.broadcast ? (
                <button
                  type="button"
                  aria-expanded={openList.includes(todo.id)}
                  onClick={() =>
                    setOpenList((was) =>
                      was.includes(todo.id)
                        ? was.filter((one) => one !== todo.id)
                        : [...was, todo.id],
                    )
                  }
                  className={
                    "shrink-0 rounded-[var(--radius)] border border-[var(--border-strong)] " +
                    "bg-[var(--surface-2)] px-1.5 py-0.5 text-xs text-[var(--text-2)] " +
                    "hover:border-[var(--accent)] hover:text-[var(--text)]"
                  }
                >
                  {todo.broadcast.done}/{todo.broadcast.total} 完成
                  <ChevronDown
                    aria-hidden="true"
                    size={12}
                    className={
                      "ml-0.5 inline align-[-1px] transition-transform " +
                      (openList.includes(todo.id) ? "rotate-180" : "")
                    }
                  />
                </button>
              ) : /* 自己寫給自己的不必顯示「某某交辦」—— 那是自己。 */
              todo.is_self_note ? null : (
                <Badge tone="neutral">
                  {todo.lane === "mine" ? `${todo.author_name} 交辦` : `給 ${todo.assignee_name}`}
                </Badge>
              )}

              <Button
                size="sm"
                disabled={!mayFinish(todo)}
                title={mayFinish(todo) ? undefined : "只有負責的同仁自己勾得掉"}
                icon={
                  todo.is_done ? (
                    <RotateCcw aria-hidden="true" size={14} />
                  ) : (
                    <Check aria-hidden="true" size={14} />
                  )
                }
                onClick={() => void toggle(todo)}
              >
                {todo.is_done ? "取消完成" : "完成"}
              </Button>
              <Button
                size="sm"
                variant="danger"
                aria-label={`刪除「${todo.title}」`}
                icon={<Trash2 aria-hidden="true" size={14} />}
                onClick={() => void remove(todo)}
              >
                刪除
              </Button>

              {/* 名單。**勾了沒不可以只靠顏色或刪節線** —— 每一列自己寫著
                  「已完成 / 還沒」。這張表的用途就是照著它去催人。 */}
              {todo.broadcast && openList.includes(todo.id) ? (
                <ul
                  aria-label={`${todo.title} 的名單`}
                  className="m-0 flex w-full list-none flex-wrap gap-x-4 gap-y-1 p-0 pt-1"
                >
                  {todo.broadcast.people.map((one) => (
                    <li key={one.user_id} className="flex items-center gap-1 text-xs">
                      <span className="text-[var(--text)]">{one.name}</span>
                      <span className={one.is_done ? "text-[var(--ok)]" : "text-[var(--text-3)]"}>
                        {one.is_done ? "已完成" : "還沒"}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
